import { useState } from "react";
import { Terminal, TriangleAlert } from "lucide-react";
import { CodePanel } from "@/components/code-panel";
import type { BlockDetail, WiringEntry } from "./types";

const TABS = ["manual", "mcp", "cli"] as const;
type Tab = (typeof TABS)[number];

const TAB_LABEL: Record<Tab, string> = {
  manual: "Manual",
  mcp: "MCP",
  cli: "CLI",
};

/**
 * Manual leads because it is the only tab that works. The other two stay
 * visible rather than commented out: the registry entry below is the
 * deliverable and these are transports over it, so listing them is the
 * honest statement of that — but they are marked, so nobody clicks one
 * expecting a command they can run.
 */
const PLANNED: readonly Tab[] = ["mcp", "cli"];

/**
 * How to get this block into your app.
 *
 * All three tabs are projections of the same registry entry — which is the
 * point of defining the format before the transport. The MCP and CLI surfaces
 * are not built yet; the manual tab always works and is what the other two
 * will automate, so it is both first and the default.
 */
export function BlockInstall({ block }: { block: BlockDetail }) {
  const [tab, setTab] = useState<Tab>("manual");

  // A block whose dependency lives on a private index (not public PyPI) needs
  // that index added to *this app's* pyproject.toml before `uv add` can
  // resolve it at all — the Dependencies panel below has no way to say that
  // on its own, and it renders above the Wiring section that actually adds
  // the index. Without this, `uv add` just fails with "was not found in the
  // package registry", which reads like the package doesn't exist rather
  // than "configure the index first". Matched on the exact target
  // "pyproject.toml" (not e.g. internal-sdk's "pyproject.toml (of the
  // extracted SDK repo)", which configures publishing for a different repo
  // entirely and has nothing to do with resolving *this* app's dependencies).
  const indexWiringStep = block.wiring.find(
    (w) => w.target === "pyproject.toml" && w.code.includes("[[tool.uv.index]]"),
  );

  // An optional pip extra (e.g. structured-logging's `[cloudwatch]`) needs a
  // different `uv add` than the one this panel shows by default — surface it
  // here too, not just in Wiring, or nobody installing top-to-bottom notices
  // the feature exists until the plain install is already done.
  const extraMatch = block.wiring
    .map((w) => ({ w, extra: /^uv add ".+\[(.+?)\]/.exec(w.code.trim())?.[1] }))
    .find((m): m is { w: WiringEntry; extra: string } => m.extra !== undefined);

  const order = [...block.dependencies.map((d) => d.slug), block.slug];
  const commands: Record<Tab, string> = {
    mcp: `Add the ${block.slug} block from qure-backend-blocks to this app.`,
    cli: order.map((slug) => `npx qure-blocks add ${slug}`).join("\n"),
    manual: [
      "git clone git@github.com:qureai/qure-backend-blocks.git /tmp/qure-blocks",
      ...order.map(
        (slug) => `cp -r /tmp/qure-blocks/registry/${slug}/files/* backend/src/myapp/`,
      ),
    ].join("\n"),
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <Terminal size={15} className="text-ink-400" />
        <h2 className="text-sm font-medium tracking-tight text-ink-50">Install</h2>
        <div className="ml-auto flex gap-1">
          {TABS.map((name) => (
            <button
              key={name}
              onClick={() => setTab(name)}
              aria-pressed={tab === name}
              className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                tab === name
                  ? "bg-ink-800 text-ink-50"
                  : "text-ink-400 hover:text-ink-200"
              }`}
            >
              {TAB_LABEL[name]}
              {PLANNED.includes(name) && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-ink-500">
                  soon
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {block.dependencies.length > 0 && (
        <p className="text-xs text-ink-400">
          Installs {block.dependencies.map((d) => d.name).join(", ")} first —{" "}
          {block.slug} depends on it.
        </p>
      )}

      <CodePanel code={commands[tab]} />

      {tab !== "manual" && (
        <p className="flex items-start gap-2 rounded-lg border border-ink-700 bg-ink-900 px-3 py-2.5 text-xs leading-relaxed text-ink-400">
          <TriangleAlert size={14} className="mt-px shrink-0" />
          <span>
            The {TAB_LABEL[tab]} surface isn&apos;t wired up yet — how blocks are
            fetched is deliberately still open. The registry format below is the
            deliverable; MCP, a CLI and a plain git copy are all transports over it.
            Use <b className="font-medium text-ink-200">Manual</b> today.
          </span>
        </p>
      )}

      {block.pip.length > 0 && (
        <div className="space-y-2 pt-1">
          <h3 className="text-xs font-medium text-ink-400">Dependencies</h3>
          {indexWiringStep && (
            <p className="flex items-start gap-2 rounded-lg border border-ink-700 bg-ink-900 px-3 py-2.5 text-xs leading-relaxed text-ink-400">
              <TriangleAlert size={14} className="mt-px shrink-0" />
              <span>
                Not on public PyPI — add the index in the{" "}
                <b className="font-medium text-ink-200">{indexWiringStep.target}</b> step
                under Wiring below first, or this fails to resolve.
              </span>
            </p>
          )}
          <CodePanel code={`uv add ${block.pip.join(" ")}`} />
          {extraMatch && (
            <>
              <p className="text-xs text-ink-400">
                Need the{" "}
                <code className="text-ink-200">{`[${extraMatch.extra}]`}</code> extra? Run this
                instead of the plain install above:
              </p>
              <CodePanel code={extraMatch.w.code} />
            </>
          )}
        </div>
      )}
    </section>
  );
}
